import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, PackageOpen, Plus } from 'lucide-react'

import type {
    ListWithdrawalNotesParams,
    WithdrawalNoteStatus,
} from '@/lib/api/packaging-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { WithdrawalNoteStatusBadge } from '@/components/warehouse/withdrawal-status-badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    usePackagingWithdrawalNotes,
    usePackagingWithdrawalNotesInfinite,
} from '@/hooks/use-packaging-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface WithdrawalNotesSearch {
    status?: WithdrawalNoteStatus
    page?: number
}

const VALID_STATUSES = ['open', 'closed', 'cancelled'] as const

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/withdrawal-notes/',
)({
    component: Page,
    validateSearch: (
        search: Record<string, unknown>,
    ): WithdrawalNotesSearch => {
        const out: WithdrawalNotesSearch = {}
        if (
            typeof search.status === 'string' &&
            VALID_STATUSES.includes(search.status as WithdrawalNoteStatus)
        ) {
            out.status = search.status as WithdrawalNoteStatus
        }
        const p =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(p) && p > 1) out.page = p
        return out
    },
})

function Page() {
    const search = Route.useSearch()
    const navigate = useNavigate({
        from: '/packaging-warehouse/withdrawal-notes/',
    })
    const setSearch = (next: WithdrawalNotesSearch) => {
        void navigate({ search: () => next, replace: false })
    }
    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const params = useMemo<ListWithdrawalNotesParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            ...(search.status ? { status: search.status } : {}),
        }),
        [search],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } =
        usePackagingWithdrawalNotes(params, { enabled: !isMobile })

    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.status ? { status: search.status } : {}),
        }),
        [search.status],
    )
    const infinite = usePackagingWithdrawalNotesInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const page = search.page ?? 1

    const filterActive = !!search.status || page > 1
    const clearFilter = () => setSearch({})

    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const renderFilters = () => (
        <div>
            <Label className="mb-1 block">{m.sl_note_filter_status()}</Label>
            <Select
                value={search.status ?? 'all'}
                onValueChange={(v) =>
                    setSearch({
                        ...search,
                        status:
                            v === 'all'
                                ? undefined
                                : (v as WithdrawalNoteStatus),
                        page: undefined,
                    })
                }
            >
                <SelectTrigger className="w-full">
                    <SelectValue placeholder={m.sl_note_filter_all_status()} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">
                        {m.sl_note_filter_all_status()}
                    </SelectItem>
                    <SelectItem value="open">
                        {m.scl_withdrawal_status_open()}
                    </SelectItem>
                    <SelectItem value="closed">
                        {m.scl_withdrawal_status_closed()}
                    </SelectItem>
                    <SelectItem value="cancelled">
                        {m.scl_withdrawal_status_cancelled()}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<PackageOpen className="size-5 shrink-0" />}
                title={m.pkl_plh_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/packaging-warehouse/withdrawal-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pkl_note_create_plh()}
                        </Link>
                    </Button>
                }
            />

            <div className="hidden md:block">{renderFilters()}</div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.pkl_plh_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pkl_note_empty()}
                    </div>
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/packaging-warehouse/withdrawal-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/packaging-warehouse/withdrawal-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <WithdrawalNoteStatusBadge
                                        status={n.status}
                                    />
                                }
                                totalLots={n.items_count ?? 0}
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_note_col_code()}</TableHead>
                            <TableHead>{m.sl_note_col_status()}</TableHead>
                            <TableHead className="text-center">
                                {m.sl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.sl_note_col_created_by()}</TableHead>
                            <TableHead>{m.sl_note_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pkl_note_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/withdrawal-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/packaging-warehouse/withdrawal-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <WithdrawalNoteStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell>
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
